import { useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import type { ChatMessage } from './equipment-ai-chat.types';
import s from './EquipmentAIChat.module.scss';
import EquipmentChatProviderCard from './EquipmentChatProviderCard';

const SUGGESTIONS = [
  "What's missing from my equipment records?",
  'What should I check before my next trip?',
  'Is anything overdue for service?',
] as const;

interface Props {
  messages: ChatMessage[];
  onRetry: (messageId: string, text: string) => void;
  historySettled: boolean;
  accessDenied: boolean;
  chipDisabled: boolean;
  onSuggestionSelect: (text: string) => void;
}

const EquipmentAIChatMessages = ({
  messages,
  onRetry,
  historySettled,
  accessDenied,
  chipDisabled,
  onSuggestionSelect,
}: Props) => {
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  if (!historySettled) {
    return null;
  }

  if (messages.length === 0) {
    if (accessDenied) {
      return null;
    }
    return (
      <div className={s.messageList}>
        <div className={s.assistantWrapper}>
          <div className={s.assistantBubble}>
            <p>Hi, I’m your equipment consultant for this vessel.</p>
            <p>I can help you:</p>
            <ul>
              <li>
                <strong>Review what’s on record</strong> — specs, models, install dates; I&apos;ll
                tell you what&apos;s missing or looks off for a vessel this size.
              </li>
              <li>
                <strong>Plan the work</strong> — what to check before a trip or a season, and
                what&apos;s overdue going by the service dates you have saved.
              </li>
              <li>
                <strong>Find someone to do it</strong> — I can search the platform&apos;s
                service-provider directory and show you the providers that fit.
              </li>
            </ul>
            <p>
              I can’t add or edit equipment myself. You do that on the page, and I’ll pick up the
              change on your next question.
            </p>
            <p>What would you like to ask first?</p>
          </div>
        </div>
        <div className={s.suggestions}>
          {SUGGESTIONS.map((text) => (
            <button
              key={text}
              type="button"
              className={s.chip}
              onClick={() => onSuggestionSelect(text)}
              disabled={chipDisabled}
              aria-label={text}
            >
              {text}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={s.messageList}>
      {messages.map((msg) =>
        msg.role === 'user' ? (
          <div key={msg.id} className={s.userBubble}>
            {msg.content}
          </div>
        ) : (
          <div key={msg.id} className={s.assistantWrapper}>
            {msg.statusLabel && <div className={s.statusLabel}>{msg.statusLabel}</div>}
            <div className={s.assistantBubble}>
              {msg.error ? (
                <div className={s.errorState}>
                  <p className={s.errorMessage}>{msg.error.message}</p>
                  {msg.error.retryable && msg.retryPayload && (
                    <button
                      className={s.retryButton}
                      onClick={() => onRetry(msg.id, msg.retryPayload!)}
                      type="button"
                    >
                      Try Again
                    </button>
                  )}
                </div>
              ) : msg.content ? (
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
              ) : msg.streaming ? (
                <span className={s.typing}>…</span>
              ) : null}
              {msg.providers && msg.providers.length > 0 && (
                <div className={s.providerCards}>
                  {msg.providers.map((p) => (
                    <EquipmentChatProviderCard key={p.id} provider={p} />
                  ))}
                </div>
              )}
            </div>
          </div>
        ),
      )}
      <div ref={endRef} />
    </div>
  );
};

export default EquipmentAIChatMessages;
